import { ReadonlySignal, Signal, computed, signal } from '@preact/signals'
import { Barcode, Symbology, SymbologyDescription, TrackedBarcode } from '@scandit/web-datacapture-barcode'

type Id = `${Barcode['data']}-${Barcode['symbology']}`

export class StorableBarcode {
  data: string | null
  symbology: Symbology
  addOnData: string | null
  id: Id
  trackedIdentifier: string = ''
  symbologyDescription: SymbologyDescription
  quantity: Signal<number>

  constructor(barcode: Barcode) {
    const { data, symbology, addOnData } = barcode
    this.data = data
    this.symbology = symbology
    this.symbologyDescription = new SymbologyDescription(symbology)
    this.addOnData = addOnData
    this.id = StorableBarcode.idForBarcode(barcode)

    this.quantity = signal(1)
  }

  public static fromTrackedBarcode(trackedBarcode: TrackedBarcode) {
    const instance = new this(trackedBarcode.barcode)
    instance.trackedIdentifier = String(trackedBarcode.identifier)
    return instance
  }

  public static areEqual(storableBarcode1: StorableBarcode, storableBarcode2: StorableBarcode) {
    return storableBarcode1.id === storableBarcode2.id
  }

  public static idForBarcode({ data, symbology }: Barcode): Id {
    return `${data}-${symbology}`
  }
}

export class BarcodeListStore {
  private map: Map<string, StorableBarcode> = new Map()
  public barcodes = signal<StorableBarcode[]>([])
  public totalQuantity: ReadonlySignal<number>

  constructor() {
    this.totalQuantity = computed(() =>
      this.barcodes.value.reduce((acc, code) => {
        acc += code.quantity.value
        return acc
      }, 0)
    )
  }

  public add(barcodes: Barcode[]) {
    for (const barcode of barcodes) {
      const key = StorableBarcode.idForBarcode(barcode)
      const alreadyPresent = this.map.get(key)
      if (alreadyPresent) {
        alreadyPresent.quantity.value += 1
      } else {
        const newStorableBarcode = new StorableBarcode(barcode)
        this.map.set(key, newStorableBarcode)
      }
    }
    // reverse cause we want last item on top
    this.barcodes.value = [...this.map.values()].reverse()
  }

  public removeAll() {
    this.map.clear()
    this.barcodes.value = [...this.map.values()]
  }

  public updateFromTrackedBarcodes(trackedBarcodes: TrackedBarcode[]) {
    for (const trackedBarcode of Object.values(trackedBarcodes)) {
      const storableTrackedBarcode = StorableBarcode.fromTrackedBarcode(trackedBarcode)
      const alreadyPresent = this.map.get(storableTrackedBarcode.id)
      if (!alreadyPresent) {
        this.map.set(storableTrackedBarcode.id, storableTrackedBarcode)
      } else {
        alreadyPresent.quantity.value += 1
      }
    }
    this.barcodes.value = [...this.map.values()].reverse()
  }
}
